import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, Button, CircularProgress, IconButton,
} from '@mui/material';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import v2Api from '../../../../utils/v2Api';
import { TestSessionProvider, useTestSession } from './TestSessionContext';
import ReviewScreen from './components/ReviewScreen';
import PdfRenderer from './renderers/PdfRenderer';
import QuestionUIRenderer from './renderers/QuestionUIRenderer';

// ── Shared prospect-facing card shell (landing / thank-you / invalid) ──
const CenterCard = ({ children, maxWidth = 460 }) => (
    <Box sx={{
        minHeight: '100vh', width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(160deg, #1F2A40, #101725)', p: 3,
    }}>
        <Box sx={{
            background: '#fff', borderRadius: '16px', maxWidth, width: '100%',
            p: '40px 44px', textAlign: 'center', boxShadow: '0 24px 70px rgba(0,0,0,.4)',
        }}>
            {children}
        </Box>
    </Box>
);

const Branded = () => (
    <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: 1, color: '#040509' }}>A ONE INSTITUTE</Typography>
);

const PlacementTakerInner = ({ token, preview }) => {
    const { hydrate, test, isDirty, save, saving, answers, submit, submitError, session } = useTestSession();

    const [phase, setPhase] = useState('loading'); // loading | landing | starting | taking | done | invalid
    const [meta, setMeta] = useState(null);
    const [invalidReason, setInvalidReason] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);

    // Landing: validate the link + get the test title.
    useEffect(() => {
        (async () => {
            try {
                const res = await v2Api.get(`/placement/${token}`, preview ? { preview: 1 } : undefined);
                const d = res.data?.data;
                if (d?.validity === 'completed') {
                    // Already submitted → always show the positive thank-you, never "unavailable".
                    setPhase('done');
                } else if (!d || d.validity !== 'ok') {
                    setInvalidReason(d?.validity || 'invalid');
                    setPhase('invalid');
                } else {
                    setMeta(d);
                    setPhase('landing');
                }
            } catch {
                setInvalidReason('invalid');
                setPhase('invalid');
            }
        })();
    }, [token, preview]);

    // Warn before accidental tab close while answers are unsaved.
    useEffect(() => {
        const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    const begin = async () => {
        try {
            setPhase('starting');
            const res = await v2Api.get(`/placement/${token}/test-taking`, preview ? { preview: 1 } : undefined);
            hydrate(res.data.data);
            setPhase('taking');
        } catch (err) {
            const v = err.response?.data?.data?.validity;
            if (v === 'completed') setPhase('done');
            else { setInvalidReason(v || 'invalid'); setPhase('invalid'); }
        }
    };

    const handleSubmit = async (gradingMode) => {
        try {
            await submit(gradingMode); // server forces immediate grading regardless
            setReviewOpen(false);
            setPhase('done');
        } catch { /* submitError shown in chrome */ }
    };

    // ── Phase: loading ──
    if (phase === 'loading' || phase === 'starting') {
        return (
            <CenterCard>
                <CircularProgress />
                <Typography sx={{ mt: 2, color: '#666' }}>
                    {phase === 'starting' ? 'Loading your test…' : 'Checking your link…'}
                </Typography>
            </CenterCard>
        );
    }

    // ── Phase: invalid / expired / completed ──
    if (phase === 'invalid') {
        const msg = {
            expired: 'This placement-test link has expired. Please contact the A ONE INSTITUTE office for a new one.',
            completed: 'This placement test has already been submitted. Thank you!',
            invalid: 'This link is not valid. Please double-check it or contact the A ONE INSTITUTE office.',
        }[invalidReason] || 'This link is no longer valid.';
        return (
            <CenterCard>
                <Branded />
                <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>Link unavailable</Typography>
                <Typography sx={{ color: '#666' }}>{msg}</Typography>
            </CenterCard>
        );
    }

    // ── Phase: landing ──
    if (phase === 'landing') {
        const resuming = meta?.status === 'in_progress';
        return (
            <CenterCard maxWidth={960}>
                <Branded />
                <Box sx={{
                    display: 'inline-block', background: '#CFFD5E', color: '#040509', fontWeight: 700,
                    fontSize: 12, px: 1.5, py: 0.5, borderRadius: '20px', mt: 1.5, mb: 0.5,
                }}>
                    PLACEMENT TEST
                </Box>
                <Typography variant="h4" sx={{ mt: 1, mb: 1 }}>{meta?.test?.title || 'Placement Test'}</Typography>
                {meta?.instructions ? (
                    <Box sx={{
                        textAlign: 'left', color: '#3a3f4a', fontSize: 14, lineHeight: 1.55,
                        whiteSpace: 'pre-wrap', maxHeight: '46vh', overflowY: 'auto',
                        background: '#f7f8fa', borderRadius: '10px', p: 2, mb: 2,
                    }}>
                        {meta.instructions}
                    </Box>
                ) : (
                    <Typography sx={{ color: '#666', mb: 2 }}>
                        This short placement test helps us recommend the right class for you. Answer as best you can.
                    </Typography>
                )}
                <Button
                    fullWidth variant="contained"
                    sx={{ background: '#CFFD5E', color: '#040509', fontWeight: 700, py: 1.5, ':hover': { background: '#bdec4d' } }}
                    onClick={begin}
                >
                    {preview ? 'Preview test' : resuming ? 'Resume test' : 'Begin test'}
                </Button>
                <Typography sx={{ fontSize: 12, color: '#9aa0aa', mt: 2 }}>
                    Your answers go to the A ONE INSTITUTE office. You won't see a score — staff will follow up.
                </Typography>
            </CenterCard>
        );
    }

    // ── Phase: done ──
    if (phase === 'done') {
        return (
            <CenterCard>
                <Box sx={{
                    width: 70, height: 70, borderRadius: '50%', background: 'rgba(76,206,172,.15)',
                    color: '#4cceac', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 36, margin: '0 auto 8px',
                }}>✓</Box>
                <Typography variant="h4" sx={{ mb: 0.5 }}>All done!</Typography>
                <Typography sx={{ color: '#666' }}>
                    Thanks for completing the placement test. Your responses have been submitted to the A ONE INSTITUTE office,
                    and a staff member will reach out about your placement soon.
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#9aa0aa', mt: 2 }}>
                    You can close this window.
                </Typography>
            </CenterCard>
        );
    }

    // ── Phase: taking ── (reuses the v2 renderers + review/submit engine)
    if (!test || !session) return null;
    const sections = test.v2_test_sections || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', flex: 1, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 700, letterSpacing: 0.5 }}>A ONE INSTITUTE</Typography>
                <Typography variant="h6" fontWeight={600} noWrap sx={{ flex: 1 }}>{test?.title}</Typography>

                {preview ? (
                    <Typography variant="caption" sx={{ color: '#a47600', fontWeight: 700, letterSpacing: 0.5 }}>
                        PREVIEW — read-only, nothing is saved
                    </Typography>
                ) : (
                    <>
                        {saving && <Typography variant="caption" color="text.secondary">Saving…</Typography>}
                        {!saving && !isDirty && answers.size > 0 && (
                            <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> Saved
                            </Typography>
                        )}
                        {isDirty && (
                            <Button size="small" variant="outlined" startIcon={<SaveOutlinedIcon />} onClick={save} disabled={saving}>
                                Save
                            </Button>
                        )}
                        <Button
                            variant="contained" size="small" startIcon={<RateReviewOutlinedIcon />}
                            onClick={() => { if (isDirty) save(); setReviewOpen(true); }}
                        >
                            Review & Submit
                        </Button>
                    </>
                )}
            </Box>

            {submitError && <Typography color="error" variant="body2" sx={{ px: 2, py: 0.5 }}>{submitError}</Typography>}

            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {test?.use_question_ui ? <QuestionUIRenderer /> : <PdfRenderer />}
            </Box>

            <ReviewScreen
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                onConfirmSubmit={handleSubmit}
                sections={sections}
                isAdmin={false}
            />
        </Box>
    );
};

const PlacementTakerPage = () => {
    const { token } = useParams();
    const preview = ['1', 'true'].includes(new URLSearchParams(window.location.search).get('preview'));
    // Auth-less transport: the token IS the authorization.
    const api = useMemo(() => ({
        save: (payload) => v2Api.post(`/placement/${token}/save`, payload),
        submit: (payload) => v2Api.post(`/placement/${token}/submit`, payload),
    }), [token]);

    return (
        <TestSessionProvider api={api}>
            <PlacementTakerInner token={token} preview={preview} />
        </TestSessionProvider>
    );
};

export default PlacementTakerPage;
